/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {vec3} from "gl-matrix";
import IAddress from "App/utils/globe/chunk_lod/adress";

/***
 * Loader for the .dat files for the geometry lod. It includes inside the points mapping, geometry data and jpg texture data with 512x512 resolution
 * @constructor
 */
export default
class DatLoader {
    private _address: IAddress;
    private _binFileSize: number;
    private _jpgFileSize: number;
    private _metaData: { col: number; nofTextureLODS: number; nc: number; layer: number, level: number; heights: number[]; bbox: { min: vec3; max: vec3 }; widths: number[]; nofFaces: number; row: number; nofPoints: number, baseAddress: string };
    private _pointsMappingABuffer: ArrayBuffer;
    private _positionsABuffer: ArrayBuffer;
    private _normalsABuffer: ArrayBuffer;
    private _texcoordsABuffer: ArrayBuffer;
    private _layersABuffer: ArrayBuffer;
    private _surfaceStyleABuffer: ArrayBuffer;
    private _indicesABuffer: ArrayBuffer;
    private _jpgABuffer: ArrayBuffer;

    constructor(aAddress: IAddress) {
        this._address = aAddress;

        this._binFileSize = 0;
        this._jpgFileSize = 0;

        this._metaData = {
            nc: -1,
            level: -1,
            row: -1,
            col: -1,
            layer: -1,
            nofTextureLODS: 0,
            widths: [],
            heights: [],
            nofPoints: 0,
            nofFaces: 0,
            bbox: {
                min: vec3.create(),
                max: vec3.create()
            },
            baseAddress: ""
        };

        this._pointsMappingABuffer = null;
        this._positionsABuffer = null;
        this._normalsABuffer = null;
        this._texcoordsABuffer = null;
        this._layersABuffer = null; // additional coordinate to texture coordinates
        this._surfaceStyleABuffer = null;
        this._indicesABuffer = null;
        this._jpgABuffer = null;
    }


    public getMetadata(): { col: number; nofTextureLODS: number; nc: number; level: number; heights: any[]; bbox: { min: vec3; max: vec3 }; widths: any[]; nofFaces: number; row: number; nofPoints: number, baseAddress: string } {
        return this._metaData;
    }

    public getLevel(): number {
        return this._metaData.level;
    }

    public getNC(): number {
        return this._metaData.nc;
    }

    public getNofTextureLODs(): number {
        return this._metaData.nofTextureLODS;
    }

    public getTextureWidths(): number[] {
        return this._metaData.widths;
    }

    public getTextureHeights(): number[] {
        return this._metaData.heights;
    }

    public getNofFaces(): number {
        return this._metaData.nofFaces;
    }

    public getNofPoints(): number {
        return this._metaData.nofPoints;
    }

    public getPointMappingBuffer(): ArrayBuffer {
        return this._pointsMappingABuffer;
    }

    public getPositions(): ArrayBuffer {
        return this._positionsABuffer;
    }

    public getNormals(): ArrayBuffer {
        return this._normalsABuffer;
    }

    public getTexcoords(): ArrayBuffer {
        return this._texcoordsABuffer;
    }

    public getLayers(): ArrayBuffer {
        return this._layersABuffer;
    }

    public getSurfaceStyles(): ArrayBuffer {
        return this._surfaceStyleABuffer;
    }

    public getIndices(): ArrayBuffer {
        return this._indicesABuffer;
    }

    public getJpgBuffer(): ArrayBuffer {
        return this._jpgABuffer;
    }

    public load(aData: Uint8Array[]): boolean {
        for (let i = 0; i < aData.length; i++) {

            let byteOffset = 0;
            let dataView = new DataView(aData[i].buffer);

            // bin and jpg files size 4 + 4
            this._binFileSize = dataView.getUint32(byteOffset, true);
            byteOffset += 4;
            this._jpgFileSize = dataView.getUint32(byteOffset, true);
            byteOffset += 4;

            // read level, layer, row, col 4 + 4 + 4 + 4 (int)
            this._metaData.nc = dataView.getUint32(byteOffset, true);
            byteOffset += 4;
            this._metaData.level = dataView.getUint32(byteOffset, true);
            byteOffset += 4;
            this._metaData.layer = dataView.getUint32(byteOffset, true);
            byteOffset += 4;
            this._metaData.row = dataView.getUint32(byteOffset, true);
            byteOffset += 4;
            this._metaData.col = dataView.getUint32(byteOffset, true);
            byteOffset += 4;

            this._metaData.bbox.min[0] = dataView.getFloat32(byteOffset, true);
            byteOffset += 4;
            this._metaData.bbox.min[1] = dataView.getFloat32(byteOffset, true);
            byteOffset += 4;
            this._metaData.bbox.min[2] = dataView.getFloat32(byteOffset, true);
            byteOffset += 4;

            this._metaData.bbox.max[0] = dataView.getFloat32(byteOffset, true);
            byteOffset += 4;
            this._metaData.bbox.max[1] = dataView.getFloat32(byteOffset, true);
            byteOffset += 4;
            this._metaData.bbox.max[2] = dataView.getFloat32(byteOffset, true);
            byteOffset += 4;

            // read nof tlods 4 (int)
            this._metaData.nofTextureLODS = dataView.getUint32(byteOffset, true);
            byteOffset += 4;

            for (let j = 0; j < this._metaData.nofTextureLODS; j++) {
                this._metaData.widths.push(dataView.getUint32(byteOffset, true));
                byteOffset += 4;
                this._metaData.heights.push(dataView.getUint32(byteOffset, true));
                byteOffset += 4;
            }

            // read nofPoints
            this._metaData.nofPoints = dataView.getUint32(byteOffset, true);
            byteOffset += 4;
            // rad nofFaces
            this._metaData.nofFaces = dataView.getUint32(byteOffset, true);
            byteOffset += 4;

            // load the points mapping

            // topl level - level = num of tree levels
            let nofMappingRecords = (Math.pow(this._metaData.nc, (this._address.patchInfo.levels - this._metaData.level)) - 1) / (this._metaData.nc - 1);

            this._pointsMappingABuffer = aData[i].buffer.slice(byteOffset, byteOffset + nofMappingRecords * 2 * 4);// * 2 is because each record has 2 int values (offset+nofpoints)
            byteOffset += nofMappingRecords * 2 * 4;

            // load the 4 bytes length positions 3 + nofPoints
            this._positionsABuffer = aData[i].buffer.slice(byteOffset, byteOffset + 3 * this._metaData.nofPoints * 4);
            byteOffset += 3 * this._metaData.nofPoints * 4;

            // normals
            this._normalsABuffer = aData[i].buffer.slice(byteOffset, byteOffset + 3 * this._metaData.nofPoints * 4);
            byteOffset += 3 * this._metaData.nofPoints * 4;

            // tex cords
            this._texcoordsABuffer = aData[i].buffer.slice(byteOffset, byteOffset + 2 * this._metaData.nofPoints * 4);
            byteOffset += 2 * this._metaData.nofPoints * 4;

            // layers
            this._layersABuffer = aData[i].buffer.slice(byteOffset, byteOffset + this._metaData.nofPoints * 4);
            byteOffset += this._metaData.nofPoints * 4;

            /*let testbuff = new Float32Array(this._layersABuffer);
            for (let x = 0; x < testbuff.length; ++x) {
                if (testbuff[x] > 0.0) {
                    let a;
                    a = testbuff[x];
                    a = 1;
                }
            }*/

            // surface style
            this._surfaceStyleABuffer = aData[i].buffer.slice(byteOffset, byteOffset + this._metaData.nofPoints * 4);
            byteOffset += this._metaData.nofPoints * 4;

            // load the 4 bytes length indices (3 * 4 * nofFaces)
            this._indicesABuffer = aData[i].buffer.slice(byteOffset, byteOffset + 3 * this._metaData.nofFaces * 4);
            byteOffset += 3 * this._metaData.nofFaces * 4;

            // load jpg buffer
            this._jpgABuffer = aData[i].buffer.slice(byteOffset, byteOffset + this._jpgFileSize);
            byteOffset += this._jpgFileSize;

            // verify the byte offset equals the input array buffer size
            if (byteOffset !== aData[i].buffer.byteLength) {
                console.log("Error when parsing the input .dat file, the byteoffset at the end of parsing does not equal the size of the file content.");
                return false;
            }

            return true; // we now support only one request in data, later there can be another data like some metadata on the terrain
        }
    }
}
